// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ErrorProvider } from './contexts/ErrorContext';
import { AuthProvider } from './contexts/AuthContext';
import { ChurchProvider } from './contexts/ChurchContext';
import { TemplateSyncProvider } from './contexts/TemplateSyncContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { GlobalErrorDisplay } from './components/common/ErrorDisplay';
import SignUp from './components/auth/SignUp';
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PlanningPage from './pages/planningPage';
import SettingsPage from './pages/SettingsPage';
import InDesignPage from './pages/InDesignPage';
import StaffPage from './pages/StaffPage';
import CatalogReviewPage from './pages/CatalogReviewPage';
import HistoryReviewPage from './pages/HistoryReviewPage';
import MusicReflectionsPage from './pages/MusicReflectionsPage';
import MusicReflectionsAdminPage from './pages/MusicReflectionsAdminPage';
import DuplicateManagementPage from './pages/DuplicateManagementPage';
import MeterManagementPage from './pages/MeterManagementPage';
import TuneManagementPage from './pages/TuneManagementPage';
import ServiceMusicTemplatesPage from './pages/ServiceMusicTemplatesPage';
import LiturgicalYearSetupPage from './pages/LiturgicalYearSetupPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import MissingFilesPage from './pages/MissingFilesPage';
// FileMatchingPage removed - functionality moved to Song Edit versions
import MostUsedSongsPage from './pages/MostUsedSongsPage';
import LectionaryPage from './pages/LectionaryPage';
import ChurchManagementPage from './pages/ChurchManagementPage';
import ChoirUpcomingServicesPage from './pages/ChoirUpcomingServicesPage';
import ServiceRecordingsPage from './pages/ServiceRecordingsPage';
import SpanishSongsPage from './pages/SpanishSongsPage';
import SeasonalAnthemPlanningPage from './pages/SeasonalAnthemPlanningPage';
import BrowsePage from './pages/BrowsePage';
import PreparePage from './pages/PreparePage';
import PreludesPage from './pages/PreludesPage';
import BrahmsRequiemPage from './pages/BrahmsRequiemPage';
import BellsPage from './pages/BellsPage';
import RehearsalReadingsPage from './pages/RehearsalReadingsPage';
import BulletinsPage from './pages/BulletinsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import HomePage from './pages/HomePage';
import MusicMinistryPage from './pages/MusicMinistryPage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VersionFooter from './components/common/VersionFooter';
import DesignExperiments from './pages/experimental/DesignExperiments';
import FacilityUsePage from './pages/FacilityUsePage';
import EventPage from './pages/EventPage';
import SeasonViewPage from './pages/SeasonView/SeasonViewPage';

const App: React.FC = () => {
  return (
    <ErrorProvider>
      <AuthProvider>
        <ChurchProvider>
          <TemplateSyncProvider>
            <ErrorBoundary>
              <Router>
            <div className="App">
              <GlobalErrorDisplay />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/music-ministry" element={<MusicMinistryPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                <Route path="/admin/catalog-review" element={<ProtectedRoute requireRole="admin"><CatalogReviewPage /></ProtectedRoute>} />
                <Route path="/admin/history-review" element={<ProtectedRoute requireRole="admin"><HistoryReviewPage /></ProtectedRoute>} />
                <Route path="/music-reflections" element={<MusicReflectionsPage />} />
                <Route path="/music-reflections/:action" element={<MusicReflectionsPage />} />
                <Route path="/admin/music-reflections" element={<ProtectedRoute requireRole="admin"><MusicReflectionsAdminPage /></ProtectedRoute>} />
                <Route path="/staff" element={<ProtectedRoute requireRole={['staff', 'admin']}><StaffPage /></ProtectedRoute>} />
                {/* Plan - Service Planning */}
                <Route path="/planning" element={
                  <ProtectedRoute>
                    <PlanningPage />
                  </ProtectedRoute>
                } />

                {/* Season - the season at a glance (replaces the Director View tab) */}
                <Route path="/season" element={
                  <ProtectedRoute>
                    <SeasonViewPage />
                  </ProtectedRoute>
                } />

                {/* Browse - Songs, Most Used, Meters, Tunes, Lectionary */}
                <Route path="/browse" element={
                  <ProtectedRoute>
                    <BrowsePage />
                  </ProtectedRoute>
                } />
                {/* Legacy /songs route - redirect to browse */}
                <Route path="/songs" element={
                  <ProtectedRoute>
                    <BrowsePage />
                  </ProtectedRoute>
                } />

                {/* Spanish songs for the 12:30, with their music (choir and viewers too) */}
                <Route path="/songs/spanish" element={
                  <ProtectedRoute viewerAllowed>
                    <SpanishSongsPage />
                  </ProtectedRoute>
                } />

                {/* Prepare - Upcoming Services, Seasonal Planning, Preludes */}
                <Route path="/prepare" element={
                  <ProtectedRoute>
                    <PreparePage />
                  </ProtectedRoute>
                } />
                <Route path="/prepare/upcoming-services" element={
                  <ProtectedRoute>
                    <ChoirUpcomingServicesPage />
                  </ProtectedRoute>
                } />
                <Route path="/prepare/recordings" element={
                  <ProtectedRoute viewerAllowed>
                    <ServiceRecordingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/prepare/rehearsal-readings" element={
                  <ProtectedRoute>
                    <RehearsalReadingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/prepare/seasonal-planning" element={
                  <ProtectedRoute>
                    <SeasonalAnthemPlanningPage />
                  </ProtectedRoute>
                } />
                {/* Brahms Requiem: the orchestra map, the score player and orchestration layers */}
                <Route path="/prepare/brahms-requiem" element={
                  <ProtectedRoute requireRole="admin">
                    <BrahmsRequiemPage />
                  </ProtectedRoute>
                } />
                <Route path="/prepare/preludes" element={
                  <ProtectedRoute>
                    <PreludesPage />
                  </ProtectedRoute>
                } />
                {/* Legacy /choir routes */}
                <Route path="/choir" element={
                  <ProtectedRoute>
                    <PreparePage />
                  </ProtectedRoute>
                } />
                <Route path="/choir/upcoming-services" element={
                  <ProtectedRoute>
                    <ChoirUpcomingServicesPage />
                  </ProtectedRoute>
                } />
                <Route path="/choir/rehearsal-readings" element={
                  <ProtectedRoute>
                    <RehearsalReadingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/choir/seasonal-planning" element={
                  <ProtectedRoute>
                    <SeasonalAnthemPlanningPage />
                  </ProtectedRoute>
                } />

                <Route path="/indesign" element={<ProtectedRoute requireRole="admin"><InDesignPage /></ProtectedRoute>} />
                {/* Admin - Settings and Configuration */}
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/duplicates" element={
                  <ProtectedRoute requireRole="admin">
                    <DuplicateManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/missing-files" element={
                  <ProtectedRoute>
                    <MissingFilesPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/service-music-templates" element={
                  <ProtectedRoute requireRole="admin">
                    <ServiceMusicTemplatesPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/liturgical-years" element={
                  <ProtectedRoute requireRole="admin">
                    <LiturgicalYearSetupPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/email-templates" element={
                  <ProtectedRoute requireRole="admin">
                    <EmailTemplatesPage />
                  </ProtectedRoute>
                } />
                <Route path="/admin/churches" element={
                  <ProtectedRoute requireRole="admin">
                    <ChurchManagementPage />
                  </ProtectedRoute>
                } />
                {/* Legacy /settings routes */}
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/duplicates" element={
                  <ProtectedRoute requireRole="admin">
                    <DuplicateManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/missing-files" element={
                  <ProtectedRoute>
                    <MissingFilesPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/meters" element={
                  <ProtectedRoute>
                    <MeterManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/tunes" element={
                  <ProtectedRoute>
                    <TuneManagementPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/service-music-templates" element={
                  <ProtectedRoute requireRole="admin">
                    <ServiceMusicTemplatesPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/liturgical-years" element={
                  <ProtectedRoute requireRole="admin">
                    <LiturgicalYearSetupPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/email-templates" element={
                  <ProtectedRoute requireRole="admin">
                    <EmailTemplatesPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/most-used-songs" element={
                  <ProtectedRoute>
                    <MostUsedSongsPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/lectionary" element={
                  <ProtectedRoute>
                    <LectionaryPage />
                  </ProtectedRoute>
                } />
                <Route path="/settings/churches" element={
                  <ProtectedRoute requireRole="admin">
                    <ChurchManagementPage />
                  </ProtectedRoute>
                } />

                {/* Bulletins */}
                <Route path="/bulletins" element={
                  <ProtectedRoute>
                    <BulletinsPage />
                  </ProtectedRoute>
                } />

                {/* Experimental Design Preview - no auth required for quick testing */}
                <Route path="/design-experiments" element={<DesignExperiments />} />

                {/* Facility Use Agreement (public) */}
                {/* Bells practice page: its own sign-in with the choir password (app/bells/README.md) */}
                <Route path="/bells" element={<BellsPage />} />
                <Route path="/facility-use" element={<FacilityUsePage />} />
                <Route path="/facility-use/r/:token" element={<FacilityUsePage />} />

                {/* Church event music for the players (public, unguessable address; app/events/README.md) */}
                <Route path="/events/:address" element={<EventPage />} />
                <Route path="/events/:address/join/:token" element={<EventPage />} />

                <Route path="*" element={<div>404 - Page Not Found</div>} />
              </Routes>
            </div>
            <ToastContainer />
            <VersionFooter />
            </Router>
          </ErrorBoundary>
          </TemplateSyncProvider>
        </ChurchProvider>
      </AuthProvider>
    </ErrorProvider>
  );
};

export default App;

